import React, { useState } from 'react';
import { 
  Box,
  Typography,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Tooltip,
  useTheme,
  Container,
  Tabs,
  Tab,
  Card,
  CardContent
} from '@mui/material';
import { tooltipStyles, tableCellStyles, avatarStyles, mainCellGap, tabContainerStyles, tabStyles } from '../theme/componentStyles';
import { tableStyles as themeTableStyles } from '../theme/tableStyles';
import { cellStyles } from '../theme/typography';
import RarityAvatar from '../components/common/RarityAvatar';
import RarityBadge from '../components/common/RarityBadge';

const StandardText = ({ children, ...props }) => (
  <Typography variant="body2" {...props}>
    {children}
  </Typography>
);

// Bed data with all requirements (previous beds, fertilizers, and $CFB tokens)
export const BED_DATA = [
  {
    id: 'cardboard',
    name: 'Cardboard',
    rarity: 'Common',
    material: 'Cardboard',
    productAmount: 'x 1',
    requirements: [], // No requirements for the first bed
    image: '/images/beds/basic/common/preview.png',
  },
  {
    id: 'wood',
    name: 'Wood',
    rarity: 'Uncommon',
    material: 'Wood Planks',
    productAmount: 'x 2',
    requirements: [
      { type: 'beds', value: '2', prevBedId: 'cardboard' },
      { type: 'fertilizer', value: '10' },
      { type: 'cfb', value: '25' }
    ],
    image: '/images/beds/basic/uncommon/preview.png',
  },
  {
    id: 'plastic',
    name: 'Plastic',
    rarity: 'Rare',
    material: 'Plastic Sheets',
    productAmount: 'x 4',
    requirements: [
      { type: 'beds', value: '2', prevBedId: 'wood' },
      { type: 'fertilizer', value: '22' },
      { type: 'cfb', value: '65' }
    ],
    image: '/images/beds/basic/rare/preview.png',
  },
  {
    id: 'metal',
    name: 'Metal',
    rarity: 'Epic',
    material: 'Metal Beams',
    productAmount: 'x 8',
    requirements: [
      { type: 'beds', value: '2', prevBedId: 'plastic' },
      { type: 'fertilizer', value: '47' },
      { type: 'cfb', value: '200' }
    ],
    image: '/images/beds/basic/epic/preview.png',
  },
  {
    id: 'stone',
    name: 'Stone',
    rarity: 'Legendary',
    material: 'Polished Stone',
    productAmount: 'x 16',
    requirements: [
      { type: 'beds', value: '2', prevBedId: 'metal' },
      { type: 'fertilizer', value: '98' },
      { type: 'cfb', value: '650' }
    ],
    image: '/images/beds/basic/legendary/preview.png'
  }
];

function BedRow({ bed, tableStyles, tooltipStyles, theme }) {
  const rowRef = React.useRef(null);
  React.useEffect(() => {
    if (rowRef.current) {
      // setRowHeight(rowRef.current.offsetHeight); // This line was removed as per the edit hint
    }
  }, [rowRef, bed]);
  return (
    <TableRow ref={rowRef}>
      <TableCell sx={{ ...tableCellStyles.root, width: '30%', height: 80, minHeight: 80, maxHeight: 80, p: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center'
        }}>
          <Tooltip title={bed.name} arrow>
            <Box>
              <RarityAvatar 
                src={bed.image}
                alt={bed.name}
                size="md"
                sx={{ ...avatarStyles.standard, margin: '0 auto' }}
                aria-label={bed.name}
              />
            </Box>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles.root, width: '20%', height: 80, minHeight: 80, maxHeight: 80, p: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 40
        }}>
          <RarityBadge rarity={bed.rarity} size="small" />
        </Box>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles.root, width: '15%', height: 80, minHeight: 80, maxHeight: 80, textAlign: 'center', p: 1 }}>
        <StandardText sx={{ fontWeight: 600, color: 'text.primary' }}>
          {bed.productAmount}
        </StandardText>
      </TableCell>
      <TableCell sx={{ ...tableCellStyles.root, width: '35%', height: 80, minHeight: 80, maxHeight: 80, p: 1 }}>
        {bed.requirements.length === 1 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            <Tooltip title={`${bed.requirements[0].type === 'beds' ? BED_DATA.find(b => b.id === bed.requirements[0].prevBedId)?.name : bed.requirements[0].type.charAt(0).toUpperCase() + bed.requirements[0].type.slice(1)} (${bed.requirements[0].type})`} arrow>
              <Box>
                <RarityAvatar 
                  src={bed.requirements[0].type === 'beds' ? `/images/beds/basic/${BED_DATA.find(b => b.id === bed.requirements[0].prevBedId)?.rarity.toLowerCase()}/preview.png` : bed.requirements[0].type === 'fertilizer' ? '/images/fertilizers/basic/common/preview.png' : '/images/icon_CFB.svg'}
                  alt={bed.requirements[0].type === 'beds' ? BED_DATA.find(b => b.id === bed.requirements[0].prevBedId)?.name : bed.requirements[0].type.charAt(0).toUpperCase() + bed.requirements[0].type.slice(1)}
                  size="md"
                  sx={{
                    ...avatarStyles.standard,
                    mr: 1,
                    ml: 1,
                    boxShadow: bed.requirements[0].type === 'cfb' ? 'none' : 3,
                    ...(bed.requirements[0].type === 'cfb' && { border: 'none' })
                  }}
                  aria-label={`${bed.requirements[0].type === 'beds' ? BED_DATA.find(b => b.id === bed.requirements[0].prevBedId)?.name : bed.requirements[0].type.charAt(0).toUpperCase() + bed.requirements[0].type.slice(1)} (${bed.requirements[0].type})`}
                />
              </Box>
            </Tooltip>
            <StandardText 
              className="requirement-text"
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary', 
                ml: 0.5, 
                whiteSpace: 'nowrap', 
                textAlign: 'center', 
                lineHeight: 1,
                minWidth: '30px',
                display: 'inline-block'
              }}
            >
              x {bed.requirements[0].value}
            </StandardText>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: mainCellGap, justifyContent: 'center', width: '100%' }}>
            {bed.requirements.map((req, i) => {
              let imgSrc = '';
              let altText = '';
              let rarity = 'common';
              if (req.type === 'beds') {
                const prevBed = BED_DATA.find(b => b.id === req.prevBedId);
                if (prevBed) {
                  imgSrc = `/images/beds/basic/${prevBed.rarity.toLowerCase()}/preview.png`;
                  altText = prevBed.name;
                  rarity = prevBed.rarity.toLowerCase();
                } else {
                  imgSrc = '/images/beds/basic/common/preview.png';
                  altText = 'Bed';
                  rarity = 'common';
                }
              } else if (req.type === 'fertilizer') {
                imgSrc = '/images/fertilizers/basic/common/preview.png';
                altText = 'Fertilizer';
                rarity = 'uncommon';
              } else {
                imgSrc = '/images/icon_CFB.svg';
                altText = '$CFB';
                rarity = 'legendary';
              }
              return (
                <Tooltip key={i} title={req.type === 'cfb' ? altText : `${altText} (${rarity})`} arrow>
                  <Box 
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: mainCellGap,
                      borderRadius: 1,
                      // Hover effect removed
                      transition: 'all 0.2s ease-in-out',
                      cursor: 'pointer',
                    }}
                  >
                    <RarityAvatar 
                      src={imgSrc}
                      alt={altText}
                      size="md"
                      sx={{
                        ...avatarStyles.standard,
                        mr: 1,
                        ml: 1,
                        boxShadow: req.type === 'cfb' ? 'none' : 3,
                        ...(req.type === 'cfb' && { border: 'none' })
                      }}
                      aria-label={`${altText} (${rarity})`}
                    />
                    <StandardText 
                      className="requirement-text"
                      sx={{ 
                        fontWeight: 600, 
                        color: 'text.primary', 
                        ml: 0.5, 
                        whiteSpace: 'nowrap', 
                        textAlign: 'left', 
                        lineHeight: 1,
                        minWidth: '30px' // Ensure minimum width for the text
                      }}
                    >
                      x {req.value}
                    </StandardText>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}

const Beds = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const tableStyles = themeTableStyles(useTheme());
  
  // Tab data matching the structure from Animals page
  const bedTabs = [
    { 
      id: 'all', 
      name: 'Beds',
      label: 'Beds',
      // Using a placeholder image that matches the style of animal tabs
      image: '/images/beds/basic/common/preview.png' 
    },
  ];

  return (
    <Box sx={{ 
      ...tabContainerStyles.outer, 
      width: '100%',
      minHeight: '100vh',
      pb: 8, // Increased bottom padding to prevent footer cutoff
      overflowX: 'auto',
      '&::-webkit-scrollbar': {
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0.1)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
      },
    }}>
      <Container maxWidth={false} disableGutters sx={{ ...tabContainerStyles.inner, width: 1200, mx: 'auto' }}>
        <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto' }}>
          {/* Tabs with matching style from Animals page */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ 
              ...tabStyles.container, 
              width: 1200,
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTabs-flexContainer': {
                justifyContent: 'center',
              },
            }}
            aria-label="beds database"
          >
            {bedTabs.map((tab, index) => (
              <Tab
                key={tab.id}
                label={
                  <Box sx={{
                    ...tabStyles.content,
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                  }}>
                    <RarityAvatar
                      src={tab.image}
                      alt={tab.name}
                      size="md"
                      sx={{
                        ...avatarStyles.standard,
                        mx: 'auto',
                        mb: 0.5,
                      }}
                    />
                    <Typography variant="body2" sx={{
                      ...tabStyles.label,
                      textAlign: 'center',
                      width: '100%',
                    }}>
                      {tab.label}
                    </Typography>
                  </Box>
                }
                sx={{
                  ...tabStyles.root,
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    '& .MuiTypography-root': {
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    },
                  },
                }}
              />
            ))}
          </Tabs>
          <TableContainer sx={{ 
            ...tableStyles.tableContainer, 
            width: '100%',
            maxWidth: 1200,
            mx: 'auto',
            overflow: 'hidden',
            boxShadow: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            background: 'transparent',
            '& .MuiTableCell-root': {
              textAlign: 'center',
              verticalAlign: 'middle',
              lineHeight: 1,
              height: 40,
              minHeight: 40,
              maxHeight: 40,
              padding: '8px 16px'
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              py: 1,
              px: 2,
              height: 40,
              minHeight: 40,
              maxHeight: 40
            },
            '&:hover': {
              boxShadow: 4
            }
          }}>
            <Table size="small" aria-label="beds database" sx={{ width: '100%' }}>
              <TableHead sx={tableStyles.tableHead}>
                <TableRow>
                  <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '30%', ...cellStyles.center }}>Bed</TableCell>
                  <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '20%', ...cellStyles.center }}>Rarity</TableCell>
                  <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '15%', ...cellStyles.center }}>Product Amount</TableCell>
                  <TableCell sx={{ ...tableStyles.tableCell, ...cellStyles.header, width: '35%', ...cellStyles.center }}>Upgrade Requirement</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={tableStyles.tableBody}>
                {BED_DATA.map((bed) => (
                  <BedRow 
                    key={bed.id} 
                    bed={bed} 
                    tableStyles={tableStyles} 
                    tooltipStyles={tooltipStyles} 
                    theme={theme} 
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        
        <Box sx={{ mb: 4, width: '100%', maxWidth: 1200, mx: 'auto' }}>
          <Card elevation={0} sx={{ 
            bgcolor: 'transparent', 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 2, // Consistent with animals page
            width: '100%',
            position: 'relative',
            top: 0,
            left: 0,
            transform: 'none !important',
            transition: 'none !important',
            '&:hover': {
              transform: 'none !important',
              boxShadow: 'none !important',
              top: 0,
              left: 0
            }
          }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" sx={{ color: 'primary.main', mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
                Upgrade Path Summary
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold' }}>Bed Requirements:</Typography>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  p: 2
                }}>
                  {BED_DATA.map((bed, index, array) => {
                    const count = Math.pow(2, array.length - 1 - index);
                    const nextBed = BED_DATA[index + 1];
                    const requirements = nextBed?.requirements || [];
                    const fertilizerReq = requirements.find(r => r.type === 'fertilizer');
                    const cfbReq = requirements.find(r => r.type === 'cfb');

                    return (
                      <Box key={bed.id} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                        py: 2,
                        borderBottom: index < array.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider'
                      }}>
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(${Math.min(count, 8)}, 40px)`,
                          gap: 1,
                          width: 'fit-content',
                          mx: 'auto',
                          '& > *': {
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'scale(1.1)'
                            }
                          }
                        }}>
                          {Array.from({ length: count }).map((_, i) => (
                            <Tooltip key={`${bed.id}-${i}`} title={`${bed.name} (${bed.rarity})`} arrow>
                              <Box sx={{ width: '100%', height: '100%' }}>
                                <RarityAvatar
                                  src={bed.image}
                                  alt={bed.name}
                                  size="md"
                                  sx={{
                                    width: '40px',
                                    height: '40px',
                                    minWidth: '40px',
                                    minHeight: '40px',
                                    '& img': {
                                      width: '40px !important',
                                      height: '40px !important',
                                      objectFit: 'contain !important',
                                      objectPosition: 'center',
                                      display: 'block !important',
                                      p: 0,
                                      margin: 'auto',
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0
                                    },
                                    position: 'relative',
                                    overflow: 'hidden',
                                    transition: 'transform 0.2s ease-in-out'
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ))}
                        </Box>

                        {/* Tier-specific requirements and merge info - like animals page */}
                        {nextBed && (fertilizerReq || cfbReq) && (
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: '100%',
                            mt: 1,
                            mb: 2
                          }}>
                            <Typography variant="body2" color="text.primary" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                              {count}x {bed.name} → {count/2}x {nextBed.name} ({count/2} merges)
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              gap: 2,
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}>
                              {fertilizerReq && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <RarityAvatar
                                    src="/images/fertilizers/basic/common/preview.png"
                                    alt="Fertilizer"
                                    size="md"
                                    sx={{
                                      width: '40px',
                                      height: '40px',
                                      '& img': {
                                        width: '40px !important',
                                        height: '40px !important',
                                        objectFit: 'contain',
                                        padding: 0
                                      }
                                    }}
                                  />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                    x {fertilizerReq.value * count/2}
                                  </Typography>
                                </Box>
                              )}
                              {cfbReq && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <RarityAvatar
                                    src="/images/icon_CFB.svg"
                                    alt="$CFB"
                                    size="md"
                                    sx={{
                                      width: '40px',
                                      height: '40px',
                                      '& img': {
                                        width: '40px !important',
                                        height: '40px !important',
                                        objectFit: 'contain',
                                        padding: '2px'
                                      }
                                    }}
                                  />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                    x {cfbReq.value * count/2}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* Dashed separator like animals page */}
                <Box sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: '1px dashed',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle1" sx={{
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    mb: 1.5,
                    color: 'text.primary',
                  }}>
                    Total Resources Needed:
                  </Typography>

                  {/* Two resource cards side by side like animals page */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    width: '100%',
                    '& > *': {
                      flex: 1
                    },
                    '@media (min-width: 600px)': {
                      flexDirection: 'row',
                      gap: 2
                    }
                  }}>
                    {/* Fertilizer */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'none',
                      minWidth: 120,
                      '&:hover': {
                        transform: 'none',
                        boxShadow: 'none'
                      }
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        width: '100%'
                      }}>
                        <RarityAvatar
                          src="/images/fertilizers/basic/common/preview.png"
                          alt="Fertilizer"
                          size="md"
                          sx={avatarStyles.standard}
                        />
                        <Typography variant="body1" fontWeight="bold" color="success.main" sx={{ fontSize: '1rem' }}>
                          x {BED_DATA.reduce((total, bed, index) => {
                            const count = Math.pow(2, BED_DATA.length - 1 - index);
                            const fertilizer = bed.requirements?.find(r => r.type === 'fertilizer')?.value || 0;
                            return total + (parseInt(fertilizer, 10) * count);
                          }, 0).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>

                    {/* CFB */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'none',
                      minWidth: 120,
                      '&:hover': {
                        transform: 'none',
                        boxShadow: 'none'
                      }
                    }}>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        width: '100%'
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: '4px',
                          '& img': {
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                            border: 'none',
                            boxShadow: 'none',
                            borderRadius: '4px'
                          }
                        }}>
                          <img
                            src="/images/icon_CFB.svg"
                            alt="$CFB Token"
                            style={{ width: '100%', height: '100%' }}
                          />
                        </Box>
                        <Typography variant="body1" fontWeight="bold" color="success.main" sx={{ fontSize: '1rem' }}>
                          x {BED_DATA.reduce((total, bed, index) => {
                            const count = Math.pow(2, BED_DATA.length - 1 - index);
                            const cfb = bed.requirements?.find(r => r.type === 'cfb')?.value || 0;
                            return total + (parseInt(cfb, 10) * count);
                          }, 0).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Note section */}
        <Box sx={{
          mt: 2,
          mb: 4,
          p: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          maxWidth: 1200,
          mx: 'auto'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{
            fontSize: '0.85rem',
            fontStyle: 'italic',
            textAlign: 'center',
            lineHeight: 1.4
          }}>
            <strong>Note:</strong> This calculation includes all intermediate beds and resources needed to upgrade from Cardboard to the highest tier in one continuous path. Each merge consumes 2 beds of the current tier + requirements to produce 1 bed of the next tier.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Beds;
